import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatCOP } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Wallet, Upload, Check, X, Clock, CircleDollarSign, Filter, FileText, Image as ImageIcon, ExternalLink, MessageCircle, Send, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import UsageTip from "@/components/UsageTip";
import PDFExportButton from "@/components/PDFExportButton";
import { useHasPerm, useAuth } from "@/context/AuthContext";

const METODOS = ["efectivo", "transferencia", "consignacion", "tarjeta", "nequi", "daviplata", "otro"];

const STATE_META = {
  pendiente: { label: "Pendiente", cls: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock },
  aprobado:  { label: "Aprobado",  cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: Check },
  rechazado: { label: "Rechazado", cls: "bg-rose-100 text-rose-700 border-rose-200", icon: X },
};

export default function PagosPage() {
  const { user } = useAuth();
  const isDirector = user?.role === "admin";
  const puedeAprobar = useHasPerm("pagos.aprobar");
  const puedeReportar = useHasPerm("pagos.reportar");
  const [items, setItems] = useState([]);
  const [resumen, setResumen] = useState({ pendientes: 0, aprobados_mes: 0, rechazados_mes: 0, monto_aprobado_mes: 0 });
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroVendedor, setFiltroVendedor] = useState("");
  const [vendedores, setVendedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rechazoOpen, setRechazoOpen] = useState(false);
  const [rechazoTarget, setRechazoTarget] = useState(null);
  const [rechazoMotivo, setRechazoMotivo] = useState("");
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [comprobanteUrl, setComprobanteUrl] = useState(null);
  const [comprobanteError, setComprobanteError] = useState(null);
  const [nuevoComentario, setNuevoComentario] = useState("");
  const [enviandoComentario, setEnviandoComentario] = useState(false);
  const [reemplazandoComprobante, setReemplazandoComprobante] = useState(false);
  const fileReemplazoRef = useRef(null);

  const puedeEditarComprobante = useMemo(() => {
    if (!detalle || !user) return false;
    if (detalle.estado === "aprobado") return false;
    const esAutor = detalle.subido_por_id === user.id;
    const esAdmin = user.role === "admin";
    // Debe ser autor (o admin) Y tener el permiso `pagos.reportar`
    // (o ser director/admin puro). Igual que en el backend.
    if (!(esAutor || esAdmin)) return false;
    return esAdmin || puedeReportar;
  }, [detalle, user, puedeReportar]);

  const reemplazarComprobante = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("El archivo supera 8 MB");
      return;
    }
    setReemplazandoComprobante(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/pagos/${detalle.id}/comprobante`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(data.estado === "pendiente"
        ? "Comprobante reemplazado. El pago volvió a estado pendiente para nueva revisión."
        : "Comprobante actualizado.");
      // Recargar detalle + comprobante
      setDetalle(data);
      if (comprobanteUrl) URL.revokeObjectURL(comprobanteUrl);
      setComprobanteUrl(null); setComprobanteError(null);
      const res = await api.get(`/pagos/${data.id}/comprobante`, { responseType: "blob" });
      const ct = data.comprobante_content_type || res.headers?.["content-type"] || "application/octet-stream";
      const blob = new Blob([res.data], { type: ct });
      setComprobanteUrl(URL.createObjectURL(blob));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo reemplazar el comprobante");
    } finally {
      setReemplazandoComprobante(false);
      if (fileReemplazoRef.current) fileReemplazoRef.current.value = "";
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroEstado) params.set("estado", filtroEstado);
      if (filtroVendedor) params.set("vendedor_id", filtroVendedor);
      const [pg, rs] = await Promise.all([
        api.get(`/pagos${params.toString() ? `?${params.toString()}` : ""}`),
        api.get("/pagos/resumen"),
      ]);
      setItems(pg.data || []);
      setResumen(rs.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudieron cargar los pagos"); }
    finally { setLoading(false); }
  }, [filtroEstado, filtroVendedor]);
  useEffect(() => { load(); }, [load]);

  // Cargar lista de vendedores para el filtro (solo director/aprobador)
  useEffect(() => {
    if (!(isDirector || puedeAprobar)) return;
    api.get("/team").then((r) => {
      const list = (r.data || []).filter((u) => u.active !== false && (u.role === "vendedor" || u.role === "admin"));
      setVendedores(list);
    }).catch(() => setVendedores([]));
  }, [isDirector, puedeAprobar]);

  // Auto-abrir el detalle cuando llegamos con ?open=<pago_id> (desde notificación)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const pagoId = searchParams.get("open");
    if (!pagoId) return;
    // Buscamos el pago (en la lista si ya está cargada, o al backend si no)
    (async () => {
      try {
        let target = items.find((p) => p.id === pagoId);
        if (!target) {
          const { data } = await api.get(`/pagos/${pagoId}`);
          target = data;
        }
        if (target) verComprobante(target);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "No se pudo abrir el pago");
      }
      const params = new URLSearchParams(searchParams);
      params.delete("open");
      setSearchParams(params, { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, items.length]);

  const aprobar = async (p) => {
    if (!confirm(`Aprobar pago de ${formatCOP(p.monto)} para ${p.pedido_numero || "pedido"}?`)) return;
    try {
      await api.post(`/pagos/${p.id}/aprobar`, {});
      toast.success("Pago aprobado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo aprobar"); }
  };

  const abrirRechazo = (p) => { setRechazoTarget(p); setRechazoMotivo(""); setRechazoOpen(true); };
  const [rechazando, setRechazando] = useState(false);
  const confirmarRechazo = async () => {
    if (!rechazoMotivo.trim()) { toast.error("Escribe un motivo"); return; }
    setRechazando(true);
    try {
      await api.post(`/pagos/${rechazoTarget.id}/rechazar`, { motivo: rechazoMotivo });
      toast.success("Pago rechazado");
      setRechazoOpen(false); setRechazoTarget(null); setRechazoMotivo("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo rechazar"); }
    finally { setRechazando(false); }
  };

  const verComprobante = async (p) => {
    // Refrescamos el detalle desde el server para traer comentarios y ct actualizados
    setDetalle(p);
    setDetalleOpen(true);
    setComprobanteUrl(null);
    setComprobanteError(null);
    setNuevoComentario("");
    try {
      const full = await api.get(`/pagos/${p.id}`);
      setDetalle(full.data);
    } catch { /* ignore, usamos p */ }
    try {
      const res = await api.get(`/pagos/${p.id}/comprobante`, { responseType: "blob" });
      // Fuerza mimetype correcto en el Blob (algunos servidores devuelven octet-stream)
      const ct = p.comprobante_content_type || res.headers?.["content-type"] || "application/octet-stream";
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: ct });
      const typedBlob = blob.type && blob.type !== "application/octet-stream" ? blob : new Blob([blob], { type: ct });
      const url = URL.createObjectURL(typedBlob);
      setComprobanteUrl(url);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "No se pudo cargar el comprobante";
      setComprobanteError(msg);
      toast.error(msg);
    }
  };

  const enviarComentario = async () => {
    const texto = nuevoComentario.trim();
    if (!texto) { toast.error("Escribe un comentario"); return; }
    setEnviandoComentario(true);
    try {
      const r = await api.post(`/pagos/${detalle.id}/comentarios`, { texto });
      setDetalle(r.data);
      setNuevoComentario("");
      toast.success("Comentario agregado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo comentar");
    } finally {
      setEnviandoComentario(false);
    }
  };

  const descargarComprobante = () => {
    if (!comprobanteUrl) return;
    const a = document.createElement("a");
    a.href = comprobanteUrl;
    a.download = detalle?.comprobante_filename || `comprobante-${detalle?.pedido_numero || detalle?.id}.${detalle?.comprobante_ext || "bin"}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div>
      <header className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Tesorería</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground mt-2">Pagos y comprobantes</h1>
          <p className="text-muted-foreground mt-2">
            {puedeAprobar || isDirector
              ? "Aprueba comprobantes, comenta y controla el saldo de cada pedido."
              : "Registra tus pagos y consulta el estado de aprobación."}
          </p>
        </div>
        <PDFExportButton
          endpoint="pagos"
          filename="beem-pagos"
          label="Exportar PDF"
          testid="export-pagos-pdf"
          fields={[
            { key: "desde", label: "Desde", type: "date" },
            { key: "hasta", label: "Hasta", type: "date" },
            { key: "estado", label: "Estado", type: "select", options: [
              { value: "pendiente", label: "Pendiente" },
              { value: "aprobado", label: "Aprobado" },
              { value: "rechazado", label: "Rechazado" },
            ] },
            { key: "metodo", label: "Método", type: "select", options: [
              { value: "Contra entrega", label: "Contra entrega" },
              { value: "Transferencia", label: "Transferencia" },
              { value: "Efectivo", label: "Efectivo" },
              { value: "Consignacion", label: "Consignación" },
              { value: "Credito", label: "Crédito" },
            ] },
          ]}
        />
      </header>

      <UsageTip storageKey="pagos-v1" steps={(() => {
        const tips = [];
        if (puedeAprobar || isDirector) {
          tips.push("Aquí ves los pagos <b>pendientes de aprobación</b>. Abre el comprobante y aprueba o rechaza con motivo.");
          tips.push("Al aprobar, el <b>saldo del pedido</b> se actualiza automáticamente y el vendedor recibe la notificación.");
        } else {
          tips.push("Registra pagos desde <b>Pedidos → Detalle → Nuevo pago</b> adjuntando foto o PDF del comprobante.");
          tips.push("Un director o aprobador revisará tu comprobante. Si lo <b>rechaza</b>, recibirás una notificación crítica con el motivo para corregirlo.");
        }
        return tips;
      })()} />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3 text-amber-600" /> Pendientes</div>
          <div className="text-3xl font-display font-semibold text-foreground tabular-nums mt-2" data-testid="kpi-pendientes">{resumen.pendientes}</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Check className="w-3 h-3 text-emerald-600" /> Aprobados este mes</div>
          <div className="text-3xl font-display font-semibold text-foreground tabular-nums mt-2" data-testid="kpi-aprobados">{resumen.aprobados_mes}</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><X className="w-3 h-3 text-rose-600" /> Rechazados</div>
          <div className="text-3xl font-display font-semibold text-foreground tabular-nums mt-2" data-testid="kpi-rechazados">{resumen.rechazados_mes}</div>
        </div>
        <div className="bg-primary/10 border border-primary/30 rounded-xl p-4">
          <div className="text-[10px] font-bold uppercase tracking-wider text-primary/80 flex items-center gap-1"><CircleDollarSign className="w-3 h-3" /> Monto aprobado</div>
          <div className="text-2xl font-display font-semibold text-primary tabular-nums mt-2" data-testid="kpi-monto">{formatCOP(resumen.monto_aprobado_mes)}</div>
        </div>
      </div>

      {/* Filtro */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Filter className="w-4 h-4 text-muted-foreground" />
        {["", "pendiente", "aprobado", "rechazado"].map((e) => (
          <button key={e || "all"} onClick={() => setFiltroEstado(e)}
            className={`h-9 px-3 rounded-full text-xs font-medium border transition-colors ${filtroEstado === e ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:bg-accent"}`}
            data-testid={`filtro-${e || "todos"}`}>
            {e === "" ? "Todos" : STATE_META[e].label}
          </button>
        ))}
        {(isDirector || puedeAprobar) && vendedores.length > 0 && (
          <>
            <span className="mx-1 text-muted-foreground/40">·</span>
            <select
              value={filtroVendedor}
              onChange={(e) => setFiltroVendedor(e.target.value)}
              className="h-9 px-3 rounded-full text-xs font-medium border border-border bg-card text-muted-foreground hover:bg-accent"
              data-testid="filtro-vendedor"
            >
              <option value="">Todos los vendedores</option>
              {vendedores.map((v) => (
                <option key={v.id} value={v.id}>{v.name || v.email}</option>
              ))}
            </select>
            {filtroVendedor && (
              <button
                onClick={() => setFiltroVendedor("")}
                className="h-9 px-3 rounded-full text-xs font-medium border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10"
                data-testid="filtro-vendedor-clear"
              >
                Limpiar vendedor
              </button>
            )}
          </>
        )}
      </div>

      {/* Tabla */}
      <div className="bg-card border border-border rounded-xl beem-table">
        <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-border text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
          <div className="col-span-3">Pedido / Cliente</div>
          <div className="col-span-2">Método</div>
          <div className="col-span-2 text-right">Monto</div>
          <div className="col-span-2">Registrado por</div>
          <div className="col-span-1">Estado</div>
          <div className="col-span-2 text-right">Acciones</div>
        </div>
        {loading && <div className="px-6 py-8 text-center text-muted-foreground/70 text-sm">Cargando...</div>}
        {!loading && items.length === 0 && (
          <div className="px-6 py-16 text-center text-muted-foreground/70">
            <Wallet className="w-8 h-8 mx-auto text-muted-foreground/50" />
            <div className="mt-3 text-sm">No hay pagos registrados{filtroEstado ? ` en estado "${STATE_META[filtroEstado]?.label}"` : ""}.</div>
          </div>
        )}
        {items.map((p) => {
          const meta = STATE_META[p.estado] || STATE_META.pendiente;
          const Icon = meta.icon;
          return (
            <div key={p.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-border/60 items-center hover:bg-muted/40 transition-colors" data-testid={`pago-row-${p.id}`}>
              <div className="col-span-3">
                <div className="text-sm font-medium text-foreground">{p.pedido_numero || p.pedido_id?.slice(0, 8)}</div>
                <div className="text-xs text-muted-foreground truncate">{p.cliente_nombre || "—"}</div>
              </div>
              <div className="col-span-2">
                <span className="text-xs px-2 py-1 rounded-full bg-muted text-foreground/80 capitalize">{p.metodo}</span>
                {p.referencia && <div className="text-[10px] text-muted-foreground mt-1 font-mono">{p.referencia}</div>}
              </div>
              <div className="col-span-2 text-right">
                <div className="text-sm font-semibold text-foreground tabular-nums">{formatCOP(p.monto)}</div>
                <div className="text-[10px] text-muted-foreground">{new Date(p.subido_at).toLocaleDateString("es-CO")}</div>
              </div>
              <div className="col-span-2">
                <div className="text-xs text-foreground">{p.subido_por_nombre || "—"}</div>
                {p.revisado_por_nombre && (
                  <div className="text-[10px] text-muted-foreground">Revisó: {p.revisado_por_nombre}</div>
                )}
              </div>
              <div className="col-span-1">
                <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border font-medium ${meta.cls}`}>
                  <Icon className="w-3 h-3" /> {meta.label}
                </span>
              </div>
              <div className="col-span-2 flex justify-end gap-1">
                <button onClick={() => verComprobante(p)} className="p-2 rounded-md hover:bg-muted text-muted-foreground/70 hover:text-foreground/80" title="Ver comprobante" data-testid={`ver-comp-${p.id}`}>
                  <FileText className="w-4 h-4" />
                </button>
                {puedeAprobar && (
                  <>
                    {p.estado !== "aprobado" && (
                      <button onClick={() => aprobar(p)} className="p-2 rounded-md hover:bg-emerald-50 text-muted-foreground/70 hover:text-emerald-700" title={p.estado === "rechazado" ? "Cambiar a aprobado" : "Aprobar"} data-testid={`aprobar-${p.id}`}>
                        <Check className="w-4 h-4" />
                      </button>
                    )}
                    {p.estado !== "rechazado" && (
                      <button onClick={() => abrirRechazo(p)} className="p-2 rounded-md hover:bg-rose-50 text-muted-foreground/70 hover:text-rose-600" title={p.estado === "aprobado" ? "Cambiar a rechazado" : "Rechazar"} data-testid={`rechazar-${p.id}`}>
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </>
                )}
                <button onClick={() => verComprobante(p)} className="p-2 rounded-md hover:bg-muted text-muted-foreground/70 hover:text-primary" title="Comentar" data-testid={`comentar-${p.id}`}>
                  <MessageCircle className="w-4 h-4" />
                  {Array.isArray(p.comentarios) && p.comentarios.length > 0 && (
                    <span className="ml-0.5 text-[10px] font-semibold text-primary">{p.comentarios.length}</span>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal rechazo */}
      <Dialog open={rechazoOpen} onOpenChange={(v) => { if (rechazando) return; setRechazoOpen(v); if (!v) { setRechazoTarget(null); setRechazoMotivo(""); } }}>
        <DialogContent data-testid="rechazo-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Rechazar pago</DialogTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {rechazoTarget?.pedido_numero} · {rechazoTarget && formatCOP(rechazoTarget.monto)}
            </p>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            <div>
              <Label>Motivo del rechazo*</Label>
              <textarea value={rechazoMotivo} onChange={(e) => setRechazoMotivo(e.target.value)}
                className="w-full mt-1 border border-border rounded-md px-3 py-2 text-sm min-h-[90px]"
                placeholder="Ej: Comprobante ilegible, monto no coincide, cuenta destino incorrecta..."
                data-testid="rechazo-motivo" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" disabled={rechazando} onClick={() => setRechazoOpen(false)}>Cancelar</Button>
              <Button onClick={confirmarRechazo} disabled={rechazando} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="rechazo-confirm">{rechazando ? "Rechazando..." : "Rechazar pago"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal detalle comprobante */}
      <Dialog open={detalleOpen} onOpenChange={(v) => {
        setDetalleOpen(v);
        if (!v) { if (comprobanteUrl) URL.revokeObjectURL(comprobanteUrl); setComprobanteUrl(null); setComprobanteError(null); setDetalle(null); setNuevoComentario(""); }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="detalle-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center justify-between gap-4">
              <span>Comprobante · {detalle?.pedido_numero}</span>
              <div className="flex items-center gap-2">
                {puedeAprobar && detalle && (
                  <>
                    {detalle.estado !== "aprobado" && (
                      <Button size="sm" onClick={() => { aprobar(detalle); setDetalleOpen(false); }}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="detalle-aprobar">
                        <Check className="w-4 h-4 mr-1" /> {detalle.estado === "rechazado" ? "Cambiar a aprobado" : "Aprobar"}
                      </Button>
                    )}
                    {detalle.estado !== "rechazado" && (
                      <Button size="sm" variant="outline" onClick={() => { setDetalleOpen(false); abrirRechazo(detalle); }}
                        className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="detalle-rechazar">
                        <X className="w-4 h-4 mr-1" /> {detalle.estado === "aprobado" ? "Cambiar a rechazado" : "Rechazar"}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-muted-foreground">Cliente:</span> <b>{detalle?.cliente_nombre}</b></div>
            <div><span className="text-muted-foreground">Monto:</span> <b className="tabular-nums">{detalle && formatCOP(detalle.monto)}</b></div>
            <div><span className="text-muted-foreground">Método:</span> <b className="capitalize">{detalle?.metodo}</b></div>
            <div><span className="text-muted-foreground">Referencia:</span> <b className="font-mono text-xs">{detalle?.referencia || "—"}</b></div>
            <div><span className="text-muted-foreground">Registrado:</span> {detalle?.subido_por_nombre}</div>
            <div><span className="text-muted-foreground">Estado:</span> <b className="capitalize">{detalle?.estado}</b></div>
          </div>
          {detalle?.motivo_rechazo && (
            <div className="mt-3 p-3 bg-rose-50 border border-rose-200 rounded-md text-sm text-rose-800">
              <b>Motivo del rechazo:</b> {detalle.motivo_rechazo}
              {puedeEditarComprobante && (
                <div className="mt-2 text-[11px] text-rose-700/80 italic">
                  Puedes subir una imagen nueva con el botón <b>Reemplazar</b> arriba del comprobante. El pago volverá a estado <b>pendiente</b> para nueva revisión.
                </div>
              )}
            </div>
          )}
          {detalle?.notas && (
            <div className="mt-3 p-3 bg-muted/40 rounded-md text-sm text-muted-foreground">
              <b>Notas:</b> {detalle.notas}
            </div>
          )}
          <div className="mt-4 border border-border rounded-lg overflow-hidden bg-muted/20 flex items-center justify-center min-h-[280px] relative">
            {!comprobanteUrl && !comprobanteError && <div className="p-8 text-sm text-muted-foreground">Cargando comprobante...</div>}
            {comprobanteError && (
              <div className="p-8 text-center">
                <X className="w-8 h-8 mx-auto text-rose-500" />
                <p className="mt-3 text-sm text-rose-700 font-medium">No se pudo cargar el comprobante</p>
                <p className="mt-1 text-xs text-muted-foreground">{comprobanteError}</p>
              </div>
            )}
            {comprobanteUrl && detalle?.comprobante_content_type?.startsWith("image/") && (
              <img src={comprobanteUrl} alt="Comprobante" className="max-w-full max-h-[500px] object-contain"
                onError={() => setComprobanteError("El archivo no es una imagen válida")} data-testid="comprobante-img" />
            )}
            {comprobanteUrl && detalle?.comprobante_content_type === "application/pdf" && (
              <div className="p-6 text-center">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground" />
                <p className="mt-3 text-sm text-muted-foreground">{detalle.comprobante_filename}</p>
                <a href={comprobanteUrl} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-primary hover:underline text-sm">
                  <ExternalLink className="w-4 h-4" /> Abrir PDF en nueva pestaña
                </a>
              </div>
            )}
            {comprobanteUrl && !detalle?.comprobante_content_type?.startsWith("image/") && detalle?.comprobante_content_type !== "application/pdf" && (
              <div className="p-6 text-center">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground" />
                <p className="mt-3 text-sm text-muted-foreground">Archivo adjunto ({detalle?.comprobante_content_type || "desconocido"})</p>
              </div>
            )}
            {comprobanteUrl && (
              <div className="absolute top-2 right-2 flex gap-2">
                {puedeEditarComprobante && (
                  <>
                    <input
                      ref={fileReemplazoRef}
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={reemplazarComprobante}
                      className="hidden"
                      data-testid="reemplazar-comp-input"
                    />
                    <button
                      onClick={() => fileReemplazoRef.current?.click()}
                      disabled={reemplazandoComprobante}
                      className="h-8 px-3 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 flex items-center gap-1 disabled:opacity-60"
                      data-testid="reemplazar-comprobante-btn"
                      title={detalle?.estado === "rechazado"
                        ? "Sube una imagen nueva. El pago volverá a pendiente para revisión."
                        : "Reemplaza la imagen del comprobante"}
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${reemplazandoComprobante ? "animate-spin" : ""}`} />
                      {reemplazandoComprobante ? "Subiendo..." : "Reemplazar"}
                    </button>
                  </>
                )}
                <button onClick={descargarComprobante}
                  className="h-8 px-3 rounded-md bg-background/90 border border-border text-xs font-medium text-foreground hover:bg-background flex items-center gap-1"
                  data-testid="comprobante-descargar">
                  <Download className="w-3.5 h-3.5" /> Descargar
                </button>
              </div>
            )}
          </div>

          {/* Comentarios */}
          <div className="mt-5 border-t border-border pt-4">
            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
              <MessageCircle className="w-3.5 h-3.5" /> Comentarios · {detalle?.comentarios?.length || 0}
            </div>
            <div className="space-y-2 max-h-[220px] overflow-y-auto" data-testid="comentarios-lista">
              {(!detalle?.comentarios || detalle.comentarios.length === 0) && (
                <div className="text-xs text-muted-foreground/70 italic">Aún no hay comentarios.</div>
              )}
              {(detalle?.comentarios || []).map((c) => (
                <div key={c.id} className="bg-muted/40 rounded-md p-3">
                  <div className="text-xs font-semibold text-foreground">{c.autor_nombre}</div>
                  <div className="text-sm text-foreground mt-1 whitespace-pre-wrap">{c.texto}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">{new Date(c.at).toLocaleString("es-CO")}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2 items-start">
              <textarea value={nuevoComentario} onChange={(e) => setNuevoComentario(e.target.value)}
                placeholder="Escribe un comentario para el equipo..."
                className="flex-1 border border-border rounded-md px-3 py-2 text-sm min-h-[60px]"
                data-testid="comentario-input" />
              <Button onClick={enviarComentario} disabled={enviandoComentario || !nuevoComentario.trim()}
                className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="comentario-enviar">
                <Send className="w-4 h-4 mr-1" /> Enviar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
